import { KM_PER_MILE } from "./constants";
import type { GarageState, Group, Item, OtherItem, Settings } from "./types";

export const GROUPS: readonly Group[] = ["Maintenance", "Repair", "Fuel", "Expense", "Other"];

/** The current app's default maintenance list. Distances in km. Names are stored in records: never rename. */
export const DEFAULT_ITEMS: readonly Readonly<Item>[] = (
  [
    ["Engine Oil & Filter", 5000, 6], ["Air Filter", 20000, 12], ["Cabin / Aircon Filter", 15000, 12], ["Fuel Filter", 40000, 24],
    ["Spark Plugs", 40000, 36], ["Coolant Flush", 40000, 24], ["Brake Fluid", 40000, 24], ["Transmission Fluid", 40000, 36],
    ["Power Steering Fluid", 40000, 36], ["Differential Oil", 40000, 36], ["Brake Pads (Front)", 30000, null], ["Brake Pads (Rear)", 40000, null],
    ["Brake Cleaning & Adjust", 10000, 6], ["Tire Rotation", 10000, 6], ["Wheel Alignment & Balancing", 20000, 12], ["Tires (Replace)", 50000, 60],
    ["Battery", null, 24], ["Drive / Fan Belt", 40000, 36], ["Timing Belt", 100000, 60], ["Wiper Blades", null, 12],
    ["Aircon Cleaning", 20000, 12], ["PMS / General Inspection", 10000, 6],
  ] as const
).map(([name, km, months]) => ({ name, km, months }));

/** The current app's other log items and their groups. Names are stored in records: never rename. */
export const DEFAULT_OTHERS: readonly Readonly<OtherItem>[] = (
  [
    ["Fuel", "Fuel"], ["General Repair", "Repair"], ["Engine Repair", "Repair"], ["Electrical", "Repair"], ["Suspension", "Repair"],
    ["Aircon Repair", "Repair"], ["Brake Repair", "Repair"], ["Body & Paint", "Repair"], ["Diagnostics", "Repair"], ["Parts Replacement (Other)", "Repair"],
    ["Registration / Road Tax", "Expense"], ["Insurance", "Expense"], ["Emissions / Safety Inspection", "Expense"], ["Toll", "Expense"], ["Parking", "Expense"],
    ["Car Wash / Detailing", "Expense"], ["Accessories", "Expense"], ["Fines / Tickets", "Expense"], ["Loan / Finance Payment", "Expense"], ["Other Expense", "Expense"],
    ["Odometer Reading", "Other"],
  ] as const
).map(([name, group]) => ({ name, group }));

/** Unit and currency choices for a new garage; the app picks them from the person's country. */
export type UnitDefaults = Pick<Settings, "currency" | "dist" | "vol" | "econ">;

const BASE_UNITS: UnitDefaults = { currency: "USD", dist: "km", vol: "L", econ: "km/L" };

/** Where odometers are in miles, new garages start from round mile intervals (still stored in km). */
export const MILE_ITEMS: Readonly<Record<string, number>> = {
  "Engine Oil & Filter": 5000, "Air Filter": 15000, "Cabin / Aircon Filter": 15000, "Fuel Filter": 30000, "Spark Plugs": 30000,
  "Coolant Flush": 30000, "Brake Fluid": 30000, "Transmission Fluid": 30000, "Power Steering Fluid": 30000, "Differential Oil": 30000,
  "Brake Pads (Front)": 20000, "Brake Pads (Rear)": 30000, "Brake Cleaning & Adjust": 6000, "Tire Rotation": 6000,
  "Wheel Alignment & Balancing": 12000, "Tires (Replace)": 30000, "Drive / Fan Belt": 30000, "Timing Belt": 60000,
  "Aircon Cleaning": 12000, "PMS / General Inspection": 6000,
};

/** An empty garage, as the current app creates it (`freshState` plus its locale wrapper). */
export function freshState(units: Partial<UnitDefaults> = {}, now: Date = new Date()): GarageState {
  const st = baseState(units, now);
  if (st.settings.dist === "mi") {
    st.settings.items = st.settings.items.map((it) => {
      const mi = MILE_ITEMS[it.name];
      return mi && it.km != null ? { ...it, km: Math.round(mi * KM_PER_MILE) } : it;
    });
  }
  return st;
}

function baseState(units: Partial<UnitDefaults>, now: Date): GarageState {
  return {
    app: "hachi-garage",
    schema: 1,
    settings: {
      ...BASE_UNITS,
      warnDist: 1000,
      warnDays: 30,
      theme: "system",
      items: DEFAULT_ITEMS.map((x) => ({ ...x })),
      others: DEFAULT_OTHERS.map((x) => ({ ...x })),
      ...units,
    },
    cars: [],
    log: [],
    meta: { created: now.toISOString(), lastBackup: null, changedSinceBackup: false, unitsConfirmed: false, hideOnboarding: false },
  };
}
